/**
 * Pure, unit-tested logic behind the Lidovi Kanban board. Unlike offers (an
 * admin-configurable stage catalog), lead stages are the FIXED 9-phase enum
 * from leadPipeline.ts, so the column set is static and every stage always
 * gets a column, in exact pipeline order. Kept out of the React page so
 * grouping/sort/sum are testable without a DOM (offersBoard.ts idiom).
 */
import type { Lead, LeadStage } from "../api/leads";
import { LEAD_STAGES } from "./leadPipeline";

/** Slice of Lead the board's pure functions need. */
export type BoardLead = Pick<Lead, "id" | "value" | "updatedAt"> & { stage: LeadStage };

export type LeadBoardColumn<L> = { stage: LeadStage; leads: L[]; sumValue: number };

/** Sum of lead value over a column; null values count as 0. */
export function sumLeadValue<L extends Pick<Lead, "value">>(leads: L[]): number {
  return leads.reduce((sum, l) => sum + (l.value ?? 0), 0);
}

/** In-column sort: most recently touched first (leads carry no due date). */
export function sortLeads<L extends Pick<Lead, "updatedAt">>(leads: L[]): L[] {
  return [...leads].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0));
}

/**
 * One column per pipeline phase (fixed order), each with its in-column sort
 * and value sum. Every stage gets a column even when empty — the board always
 * shows the whole pipeline shape.
 */
export function groupLeadsByStage<L extends BoardLead>(leads: L[]): LeadBoardColumn<L>[] {
  return LEAD_STAGES.map((stage) => {
    const inStage = sortLeads(leads.filter((l) => l.stage === stage));
    return { stage, leads: inStage, sumValue: sumLeadValue(inStage) };
  });
}
